"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { GraduationCap, Users, Award, BookOpen } from "lucide-react";

interface CountUpNumberProps {
  value: number;
  suffix: string;
}

const CountUpNumber: React.FC<CountUpNumberProps> = ({ value, suffix }) => {
  const [displayVal, setDisplayVal] = useState(0);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  useEffect(() => {
    if (inView) {
      let startTimestamp: number | null = null;
      const duration = 2000;

      const step = (timestamp: number) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const ease = progress * (2 - progress);
        setDisplayVal(Math.floor(ease * value));

        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          setDisplayVal(value);
        }
      };

      window.requestAnimationFrame(step);
    }
  }, [inView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {displayVal.toLocaleString()}
      {suffix}
    </span>
  );
};

export const WhyChooseShivalik: React.FC = () => {
  const { ref: sectionRef, inView: sectionInView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const statistics = [
    {
      value: 5000,
      suffix: "+",
      label: "Students",
      icon: <GraduationCap className="w-6 h-6 text-[#D72638]" />,
      description: "Active vibrant scholar community",
    },
    {
      value: 250,
      suffix: "+",
      label: "Faculty",
      icon: <Users className="w-6 h-6 text-indigo-400" />,
      description: "Renowned academic & industry mentors",
    },
    {
      value: 95,
      suffix: "%",
      label: "Placements",
      icon: <Award className="w-6 h-6 text-emerald-400" />,
      description: "Consistent placement in Fortune 500 companies",
    },
    {
      value: 35,
      suffix: "+",
      label: "Programs",
      icon: <BookOpen className="w-6 h-6 text-blue-400" />,
      description: "Future-oriented degree & research courses",
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-24 md:py-32 bg-[#050B14] text-white overflow-hidden border-t border-white/10">
      {/* Subtle Background Glow Orbs */}
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-[#D72638]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#B5121B]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={sectionInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center max-w-3xl mx-auto mb-16 md:mb-20"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold uppercase tracking-widest text-[#D72638] mb-4">
            Why Choose Shivalik
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl tracking-tight text-white mb-5">
            Excellence & <span className="bg-gradient-to-r from-white via-white to-[#CBD5E1] bg-clip-text text-transparent">Distinction</span>
          </h2>
          <p className="font-sans text-slate-400 text-base sm:text-lg leading-relaxed font-light">
            Empowering students with cutting-edge academic programs, world-class mentorship, and market-leading career pathways.
          </p>
        </motion.div>

        {/* Animated Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statistics.map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              animate={sectionInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6, scale: 1.02 }}
              className="relative p-6 sm:p-8 rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-white/10 hover:border-white/20 hover:bg-white/[0.06] transition-all duration-300 shadow-xl group"
            >
              <div className="p-3 rounded-xl bg-white/5 border border-white/10 w-fit mb-5 group-hover:scale-110 transition-transform duration-300">
                {stat.icon}
              </div>

              <h3 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white mb-2 tracking-tight">
                <CountUpNumber value={stat.value} suffix={stat.suffix} />
              </h3>

              <p className="font-sans font-bold text-sm text-white tracking-wide uppercase mb-1">
                {stat.label}
              </p>

              <p className="font-sans text-xs text-slate-400 font-light leading-relaxed">
                {stat.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseShivalik;
